import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ChipGroup } from "@/components/ChipGroup";
import { FollowUpPicker } from "@/components/FollowUpPicker";
import { MeetingBadge } from "@/components/Badges";
import { api, errMsg } from "@/lib/api";
import { OUTCOMES, LOSS_REASONS, label, fmtDT, toLocalInput, presetAt } from "@/lib/constants";
import { CheckCircle2, UserX, CalendarClock, XCircle, PhoneOff, Loader2 } from "lucide-react";

const MEETING_OPTS = [
  { k: "COMPLETED", t: "Done", icon: CheckCircle2, c: "emerald" },
  { k: "NO_SHOW", t: "No Show", icon: UserX, c: "amber" },
  { k: "RESCHEDULED", t: "Reschedule", icon: CalendarClock, c: "purple" },
  { k: "CANCELLED", t: "Cancel", icon: XCircle, c: "slate" },
];
const LEAD_OPTS = [
  { k: "DONE", t: "Done", icon: CheckCircle2, c: "emerald" },
  { k: "NO_ANSWER", t: "No Answer", icon: PhoneOff, c: "amber" },
  { k: "RESCHEDULE", t: "Reschedule", icon: CalendarClock, c: "purple" },
  { k: "LOST", t: "Lost", icon: XCircle, c: "rose" },
];
const TONE = {
  emerald: "border-emerald-500 bg-emerald-500/15 text-emerald-700",
  amber: "border-amber-500 bg-amber-500/15 text-amber-700",
  purple: "border-purple-500 bg-purple-500/15 text-purple-700",
  slate: "border-neutral-500 bg-neutral-100 text-neutral-800",
  rose: "border-rose-500 bg-rose-50 text-rose-700",
};

const Field = ({ title, children }) => (
  <div className="space-y-2"><p className="font-mono text-[11px] uppercase tracking-wider text-neutral-500">{title}</p>{children}</div>
);

export const DispositionDrawer = ({ target, onClose, onDone }) => {
  const [choice, setChoice] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const meeting = target?.meeting;
  const lead = target?.lead;

  useEffect(() => { setChoice(null); setForm({}); setNote(""); setErr(""); }, [target]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const opts = meeting ? MEETING_OPTS : LEAD_OPTS;
  const lossNeeded = choice === "LOST" || (choice === "COMPLETED" && form.outcome === "REJECTED");
  const followNeeded = !lossNeeded && ["DONE", "NO_ANSWER", "RESCHEDULE", "COMPLETED"].includes(choice);

  const submit = async () => {
    setErr("");
    if (note.trim().length < 3) return setErr("A concise note is required before closing.");
    if (choice === "COMPLETED" && !form.outcome) return setErr("Select the meeting outcome.");
    if (lossNeeded && !form.loss_reason) return setErr("Loss reason is mandatory.");
    if (followNeeded && !form.next_action_at) return setErr("Pick the next follow-up time.");
    if (choice === "RESCHEDULED" && !form.new_start) return setErr("Pick the new meeting time.");
    setBusy(true);
    try {
      if (meeting) {
        const body = { status: choice, notes: note, ...form };
        if (choice === "RESCHEDULED") {
          const s = new Date(form.new_start);
          body.new_start = s.toISOString();
          body.new_end = new Date(s.getTime() + (form.duration || 45) * 60000).toISOString();
        }
        await api.post(`/meetings/${meeting.id}/disposition`, body);
      } else {
        await api.post(`/leads/${lead.id}/disposition`, { outcome: choice, note, channel: target.channel === "WHATSAPP" ? "WHATSAPP" : "CALL", ...form });
      }
      toast.success("Disposition logged");
      onDone();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent data-testid="disposition-drawer" className="border-neutral-200 bg-white text-[#1A1A1A]">
        <div className="mx-auto w-full max-w-lg overflow-y-auto px-4 pb-6" style={{ maxHeight: "80vh" }}>
          <DrawerHeader className="px-0 text-left">
            <DrawerTitle className="font-display text-lg">{meeting ? "Log meeting outcome" : "Quick disposition"}</DrawerTitle>
            <DrawerDescription className="text-neutral-500">
              {lead?.full_name} · {lead?.company_name || "Individual"}
            </DrawerDescription>
            {meeting && (
              <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
                <MeetingBadge state={meeting.state} /> {label(meeting.meeting_type)} · {fmtDT(meeting.scheduled_start)}
              </div>
            )}
          </DrawerHeader>
          <div className="grid grid-cols-4 gap-2">
            {opts.map(({ k, t, icon: Icon, c }) => (
              <button key={k} type="button" data-testid={`disposition-btn-${k.toLowerCase().replace(/_/g, "-")}`} onClick={() => setChoice(k)}
                className={`flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-2xl border text-xs font-semibold transition-colors duration-150 ${choice === k ? TONE[c] : "border-neutral-200 bg-white text-neutral-600"}`}>
                <Icon className="h-5 w-5" />{t}
              </button>
            ))}
          </div>
          {choice && (
            <div className="mt-5 space-y-4">
              {choice === "COMPLETED" && (
                <Field title="Outcome"><ChipGroup options={OUTCOMES} value={form.outcome} onChange={(v) => set("outcome", v)} testPrefix="outcome" getLabel={label} /></Field>
              )}
              {choice === "NO_SHOW" && (
                <Field title="Primary cause"><ChipGroup options={["Unreachable", "Rescheduling Requested", "Technical Issue", "Other"]} value={form.no_show_cause} onChange={(v) => set("no_show_cause", v)} testPrefix="noshow-cause" /></Field>
              )}
              {choice === "RESCHEDULED" && (
                <>
                  <Field title="New date & time">
                    <input type="datetime-local" data-testid="reschedule-datetime-input" defaultValue="" onChange={(e) => set("new_start", e.target.value)}
                      min={toLocalInput(new Date())} className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm" />
                    <button type="button" data-testid="reschedule-tomorrow-chip" className="text-xs text-[#1F9E7A] underline" onClick={() => { const v = toLocalInput(presetAt("tomorrow")); set("new_start", v); document.querySelector('[data-testid="reschedule-datetime-input"]').value = v; }}>Use tomorrow 10:00</button>
                  </Field>
                  <Field title="Requested by"><ChipGroup options={["CLIENT", "SALES_EXEC"]} value={form.reschedule_source} onChange={(v) => set("reschedule_source", v)} testPrefix="reschedule-source" getLabel={label} /></Field>
                </>
              )}
              {choice === "CANCELLED" && (
                <>
                  <Field title="Origin"><ChipGroup options={["CLIENT", "INTERNAL"]} value={form.cancel_origin} onChange={(v) => set("cancel_origin", v)} testPrefix="cancel-origin" getLabel={label} /></Field>
                  <Field title="Category"><ChipGroup options={["Budget Freeze", "Scope Change", "Not Interested", "Duplicate Booking", "Other"]} value={form.cancel_category} onChange={(v) => set("cancel_category", v)} testPrefix="cancel-category" /></Field>
                </>
              )}
              {lossNeeded && (
                <Field title="Loss reason (mandatory)"><ChipGroup options={LOSS_REASONS} value={form.loss_reason} onChange={(v) => set("loss_reason", v)} testPrefix="loss-reason" /></Field>
              )}
              {(followNeeded || choice === "NO_SHOW" || choice === "CANCELLED") && (
                <Field title={followNeeded ? "Next follow-up (required)" : "Next follow-up (optional)"}>
                  <FollowUpPicker value={form.next_action_at} onChange={(v) => set("next_action_at", v)} />
                </Field>
              )}
              <Field title="Notes (required)">
                <Textarea data-testid="disposition-note-textarea" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                  placeholder="Scope summary, client response, commitments…" className="border-neutral-200 bg-white text-[#1A1A1A]" />
              </Field>
              {err && <p data-testid="disposition-error" className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}
              <Button data-testid="disposition-submit-btn" onClick={submit} disabled={busy} className="h-12 w-full rounded-2xl bg-[#1F9E7A] text-base font-semibold hover:bg-[#178566]">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save disposition"}
              </Button>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
